import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import useCropCalculations from "../../../core/hooks/useCropCalculations";
import { Z_INDEX } from "../../../core/utils/zIndexScale";
import { useEdit } from "../../context/EditContext";
import { typography, textColors } from "../../../design-system/tokens";

/**
 * InteractiveCropField - Real cropping interface with rectangular selection
 * 
 * Provides an intuitive interface for selecting crop areas directly on the image.
 * Uses crop_left, crop_top, crop_width, crop_height for real cropping without zoom.
 */
export function InteractiveCropField({ label, help, value, onChange, allValues }) {
  const DEBUG_CROP = process.env.NODE_ENV !== 'production';
  const [previewDimensions, setPreviewDimensions] = useState({ width: 400, height: 300 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState(1);
  const { saveStatus } = useEdit();
  const pendingCropSaveRef = useRef(false);
  const isSavingCrop = saveStatus === "saving" && pendingCropSaveRef.current;
  
  // Crop selection state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [initialCropArea, setInitialCropArea] = useState(null);
  const [tempCropArea, setTempCropArea] = useState(null);
  
  // Overlay state (grid removed; overlay always visible when image exists)
  
  // Undo/History removed
  
  // Manual input validation state
  const [inputValidations, setInputValidations] = useState({
    crop_left: { isValid: true, hasWarning: false, errorMessage: null },
    crop_top: { isValid: true, hasWarning: false, errorMessage: null },
    crop_width: { isValid: true, hasWarning: false, errorMessage: null },
    crop_height: { isValid: true, hasWarning: false, errorMessage: null },
  });
  
  const previewContainerRef = useRef(null);
  const imageRef = useRef(null);

  // Get current image from allValues for preview
  const currentImageUrl = allValues?.image_url;
  const currentCroppedUrl = allValues?.cropped_image_url;
  const currentFormat = allValues?.format || "original";

  if (DEBUG_CROP) {
    // eslint-disable-next-line no-console
    console.log('[InteractiveCropField] Received value:', value);
    // eslint-disable-next-line no-console
    console.log('[InteractiveCropField] AllValues:', allValues);
  }

  // Helper function to get a simple rectangular overlay style (not shape-aware)
  const getRectOverlayStyle = useCallback((cropArea) => ({
    position: 'absolute',
    left: `${cropArea.left}%`,
    top: `${cropArea.top}%`,
    width: `${cropArea.width}%`,
    height: `${cropArea.height}%`,
    border: '2px solid rgba(59, 130, 246, 0.8)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    zIndex: Z_INDEX.EDIT_OVERLAY,
  }), []);

  // Get the crop calculations using our hook with complete image data
  // Combine allValues with current crop_viewport for real-time updates
  const completeImageData = useMemo(() => ({
    ...allValues,
    crop_viewport: value // Current crop viewport being edited
  }), [allValues, value]);

  const { imageStyles } = useCropCalculations(completeImageData);

  // Extract current crop area with defaults
  const currentCropArea = {
    crop_left: value?.crop_left ?? 0,
    crop_top: value?.crop_top ?? 0,
    crop_width: value?.crop_width ?? 100,
    crop_height: value?.crop_height ?? 100,
  };

  // Current crop area is directly available from the crop viewport
  const activeArea = tempCropArea || currentCropArea;
  const visibleCropArea = {
    left: activeArea.crop_left,
    top: activeArea.crop_top,
    width: activeArea.crop_width,
    height: activeArea.crop_height,
  };

  // Default crop area for reset (no crop = full image)
  const defaultCropArea = {
    crop_left: 0,
    crop_top: 0,
    crop_width: 100,
    crop_height: 100,
  };

  // Helper to compare crop areas with small tolerance
  const areAreasEqual = useCallback((a, b) => {
    if (!a || !b) return false;
    const tol = 0.01;
    return (
      Math.abs(a.crop_left - b.crop_left) <= tol &&
      Math.abs(a.crop_top - b.crop_top) <= tol &&
      Math.abs(a.crop_width - b.crop_width) <= tol &&
      Math.abs(a.crop_height - b.crop_height) <= tol
    );
  }, []);

  // Clear pending crop save flag after save completes or errors
  useEffect(() => {
    if (saveStatus !== "saving" && pendingCropSaveRef.current) {
      pendingCropSaveRef.current = false;
    }
  }, [saveStatus]);

  // Check if current crop is different from default (no crop)
  const hasChanges = useCallback(() => {
    return (
      Math.abs(currentCropArea.crop_left - defaultCropArea.crop_left) > 0.1 ||
      Math.abs(currentCropArea.crop_top - defaultCropArea.crop_top) > 0.1 ||
      Math.abs(currentCropArea.crop_width - defaultCropArea.crop_width) > 0.1 ||
      Math.abs(currentCropArea.crop_height - defaultCropArea.crop_height) > 0.1
    );
  }, [currentCropArea]);

  // Reset to default crop area (no crop)
  const handleReset = useCallback(() => {
    if (!hasChanges()) return;
    const resetCropArea = {
      id: value?.id || null,
      ...defaultCropArea,
    };
    
    onChange(resetCropArea);
  }, [hasChanges, currentCropArea, onChange, value?.id]);

  // Enhanced onChange that tracks history
  const handleCropChange = useCallback((newCropArea) => {
    onChange(newCropArea);
  }, [onChange]);

  // Validate crop area input with intelligent auto-correction
  const validateCropAreaInput = useCallback((field, newValue, currentArea) => {
    const result = {
      isValid: true,
      correctedValue: newValue,
      errorMessage: null,
      hasWarning: false,
    };

    // Basic range validation
    if (field === 'crop_left' || field === 'crop_top') {
      if (newValue < 0) {
        result.correctedValue = 0;
        result.hasWarning = true;
        result.errorMessage = 'Value cannot be negative';
      } else if (newValue > 100) {
        result.correctedValue = 100;
        result.hasWarning = true;
        result.errorMessage = 'Value cannot exceed 100%';
      }
    } else if (field === 'crop_width' || field === 'crop_height') {
      if (newValue < 1) {
        result.correctedValue = 1;
        result.hasWarning = true;
        result.errorMessage = 'Minimum size is 1%';
      } else if (newValue > 100) {
        result.correctedValue = 100;
        result.hasWarning = true;
        result.errorMessage = 'Maximum size is 100%';
      }
    }

    // Advanced boundary validation with auto-correction
    const testArea = {
      ...currentArea,
      [field]: result.correctedValue,
    };

    // Check if area would exceed image boundaries
    if (testArea.crop_left + testArea.crop_width > 100) {
      if (field === 'crop_left') {
        result.correctedValue = Math.max(0, 100 - testArea.crop_width);
        result.hasWarning = true;
        result.errorMessage = 'Adjusted to stay within image bounds';
      } else if (field === 'crop_width') {
        result.correctedValue = Math.max(1, 100 - testArea.crop_left);
        result.hasWarning = true;
        result.errorMessage = 'Adjusted to stay within image bounds';
      }
    }

    if (testArea.crop_top + testArea.crop_height > 100) {
      if (field === 'crop_top') {
        result.correctedValue = Math.max(0, 100 - testArea.crop_height);
        result.hasWarning = true;
        result.errorMessage = 'Adjusted to stay within image bounds';
      } else if (field === 'crop_height') {
        result.correctedValue = Math.max(1, 100 - testArea.crop_top);
        result.hasWarning = true;
        result.errorMessage = 'Adjusted to stay within image bounds';
      }
    }

    return result;
  }, []);

  // Handle crop area value changes with validation
  const updateCropAreaValue = useCallback((field, inputValue, options = {}) => {
    const { skipValidation = false, silent = false } = options;
    
    let newValue = parseFloat(inputValue);
    if (isNaN(newValue)) {
      newValue = field === 'crop_width' || field === 'crop_height' ? 1 : 0;
    }

    // Apply validation if not skipped
    let finalValue = newValue;
    let validation = { isValid: true, hasWarning: false, errorMessage: null };
    
    if (!skipValidation) {
      validation = validateCropAreaInput(field, newValue, currentCropArea);
      finalValue = validation.correctedValue;
    }

    const updatedCropArea = {
      id: value?.id || null,
      crop_left: field === 'crop_left' ? finalValue : currentCropArea.crop_left,
      crop_top: field === 'crop_top' ? finalValue : currentCropArea.crop_top,
      crop_width: field === 'crop_width' ? finalValue : currentCropArea.crop_width,
      crop_height: field === 'crop_height' ? finalValue : currentCropArea.crop_height,
    };
    
    // Update validation state for real-time feedback
    setInputValidations(prev => ({
      ...prev,
      [field]: validation,
    }));

    if (!silent && DEBUG_CROP) {
      // eslint-disable-next-line no-console
      console.log(`[InteractiveCropField] Updating ${field} to ${finalValue}${validation.hasWarning ? ' (corrected)' : ''}. Full crop area:`, updatedCropArea);
      if (validation.hasWarning) {
        // eslint-disable-next-line no-console
        console.log(`[InteractiveCropField] Validation warning: ${validation.errorMessage}`);
      }
    }
    
    handleCropChange(updatedCropArea);
    return validation;
  }, [value, currentCropArea, handleCropChange, validateCropAreaInput]);

  // Helper function to get input styling based on validation state
  const getInputClassName = useCallback((field) => {
    const validation = inputValidations[field];
    let baseClasses = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors";
    
    if (validation.hasWarning) {
      return `${baseClasses} border-yellow-500 focus:ring-yellow-500 focus:border-yellow-500 bg-yellow-50`;
    } else if (!validation.isValid) {
      return `${baseClasses} border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50`;
    } else {
      return `${baseClasses} border-gray-300 focus:ring-blue-500 focus:border-blue-500`;
    }
  }, [inputValidations]);

  // Enhanced input handlers with validation feedback
  const handleInputChange = useCallback((field, value) => {
    updateCropAreaValue(field, value, { silent: true });
  }, [updateCropAreaValue]);

  const handleInputBlur = useCallback((field, e) => {
    // On blur, apply validation and correction
    const value = parseFloat(e.target.value) || (field === 'crop_width' || field === 'crop_height' ? 1 : 0);
    updateCropAreaValue(field, value);
  }, [updateCropAreaValue]);

  const handleInputKeyDown = useCallback((field, e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Trigger onBlur for validation
    } else if (e.key === 'Escape') {
      // Reset to current value
      e.target.value = currentCropArea[field].toFixed(1);
      e.target.blur();
    }
  }, [currentCropArea]);

  // Calculate preview dimensions for final result comparison
  const finalPreviewDimensions = useMemo(() => {
    if (!isImageLoaded) return { width: 200, height: 150 };
    
    // Base size for comparison previews  
    const baseSize = 200;
    let width, height;
    
    if (imageAspectRatio > 1) {
      width = baseSize;
      height = baseSize / imageAspectRatio;
    } else {
      height = baseSize;
      width = baseSize * imageAspectRatio;
    }
    
    return { width: Math.round(width), height: Math.round(height) };
  }, [isImageLoaded, imageAspectRatio]);

  // Generate final result styles with all transformations
  const finalResultStyles = useCropCalculations(
    currentImageUrl && isImageLoaded ? completeImageData : null
  );

  // Transformation summary removed

  // Handle resize operations
  const handleResizeStart = useCallback((handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSavingCrop) return;
    
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setInitialCropArea({ ...currentCropArea });
    setTempCropArea({ ...currentCropArea });
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = getResizeCursor(handle);
  }, [currentCropArea, isSavingCrop]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeHandle || !previewContainerRef.current) return;
    
    const container = previewContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate pixel delta from resize start
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    
    // Convert pixel delta to percentage delta
    const deltaXPercent = (deltaX / containerRect.width) * 100;
    const deltaYPercent = (deltaY / containerRect.height) * 100;
    
    // Calculate new crop area based on handle type
    const newCropArea = calculateNewCropArea(
      initialCropArea,
      resizeHandle,
      deltaXPercent,
      deltaYPercent
    );
    
    // Apply the new crop area if valid
    if (isValidCropArea(newCropArea)) {
      setTempCropArea({ ...newCropArea });
    }
  }, [isResizing, resizeHandle, dragStartPos, initialCropArea]);

  // Drag-to-move (keep size, move within bounds)
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!previewContainerRef.current) return;
    if (isSavingCrop) return;
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setInitialCropArea({ ...currentCropArea });
    setTempCropArea({ ...currentCropArea });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'move';
  }, [currentCropArea, isSavingCrop]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || !previewContainerRef.current || !initialCropArea) return;
    const containerRect = previewContainerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    const deltaXPercent = (deltaX / containerRect.width) * 100;
    const deltaYPercent = (deltaY / containerRect.height) * 100;

    // Keep size, move left/top within bounds
    let newLeft = initialCropArea.crop_left + deltaXPercent;
    let newTop = initialCropArea.crop_top + deltaYPercent;
    const width = initialCropArea.crop_width;
    const height = initialCropArea.crop_height;

    newLeft = Math.max(0, Math.min(100 - width, newLeft));
    newTop = Math.max(0, Math.min(100 - height, newTop));

    const moved = {
      crop_left: newLeft,
      crop_top: newTop,
      crop_width: width,
      crop_height: height,
    };
    setTempCropArea(moved);
  }, [isDragging, previewContainerRef, initialCropArea, dragStartPos]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (tempCropArea) {
      if (!areAreasEqual(tempCropArea, currentCropArea)) {
        pendingCropSaveRef.current = true;
        onChange({ id: value?.id || null, ...tempCropArea });
      }
      setTempCropArea(null);
    }
  }, [isDragging, onChange, tempCropArea, value?.id, areAreasEqual, currentCropArea]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeHandle(null);
    
    // Restore cursor and text selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (tempCropArea) {
      if (!areAreasEqual(tempCropArea, currentCropArea)) {
        pendingCropSaveRef.current = true;
        onChange({ id: value?.id || null, ...tempCropArea });
      }
      setTempCropArea(null);
    }
  }, [isResizing, onChange, tempCropArea, value?.id, areAreasEqual, currentCropArea]);

  // Helper function to get resize cursor for handle
  const getResizeCursor = useCallback((handle) => {
    const cursors = {
      'nw': 'nw-resize',
      'ne': 'ne-resize',
      'sw': 'sw-resize',
      'se': 'se-resize',
      'n': 'n-resize',
      's': 's-resize',
      'e': 'e-resize',
      'w': 'w-resize',
    };
    return cursors[handle] || 'default';
  }, []);

  // Helper function to calculate new crop area during resize (diagonal only, preserve aspect ratio)
  const calculateNewCropArea = useCallback((initial, handle, deltaXPercent, deltaYPercent) => {
    const ratio = initial.crop_width > 0 && initial.crop_height > 0
      ? initial.crop_width / initial.crop_height
      : 1;

    // Choose dominant delta to drive proportional resize
    const useX = Math.abs(deltaXPercent) >= Math.abs(deltaYPercent);

    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

    let newLeft = initial.crop_left;
    let newTop = initial.crop_top;
    let newWidth = initial.crop_width;
    let newHeight = initial.crop_height;

    if (handle === 'se') {
      if (useX) {
        newWidth = initial.crop_width + deltaXPercent;
        newHeight = newWidth / ratio;
      } else {
        newHeight = initial.crop_height + deltaYPercent;
        newWidth = newHeight * ratio;
      }
    } else if (handle === 'ne') {
      if (useX) {
        newWidth = initial.crop_width + deltaXPercent;
        newHeight = newWidth / ratio;
      } else {
        newHeight = initial.crop_height - deltaYPercent; // moving up reduces height
        newWidth = newHeight * ratio;
      }
      newTop = initial.crop_top + (initial.crop_height - newHeight);
    } else if (handle === 'sw') {
      if (useX) {
        newWidth = initial.crop_width - deltaXPercent; // moving right decreases width
        newHeight = newWidth / ratio;
      } else {
        newHeight = initial.crop_height + deltaYPercent;
        newWidth = newHeight * ratio;
      }
      newLeft = initial.crop_left + (initial.crop_width - newWidth);
    } else if (handle === 'nw') {
      if (useX) {
        newWidth = initial.crop_width - deltaXPercent;
        newHeight = newWidth / ratio;
      } else {
        newHeight = initial.crop_height - deltaYPercent;
        newWidth = newHeight * ratio;
      }
      newLeft = initial.crop_left + (initial.crop_width - newWidth);
      newTop = initial.crop_top + (initial.crop_height - newHeight);
    } else if (handle === 'n') {
      // Top handle: adjust height and top position
      newHeight = initial.crop_height - deltaYPercent;
      newTop = initial.crop_top + deltaYPercent;
      newWidth = initial.crop_width; // keep width unchanged
      newLeft = initial.crop_left; // keep left unchanged
    } else if (handle === 's') {
      // Bottom handle: adjust height only
      newHeight = initial.crop_height + deltaYPercent;
      newWidth = initial.crop_width; // keep width unchanged
      newTop = initial.crop_top; // keep top unchanged
      newLeft = initial.crop_left; // keep left unchanged
    } else if (handle === 'e') {
      // Right handle: adjust width only
      newWidth = initial.crop_width + deltaXPercent;
      newHeight = initial.crop_height; // keep height unchanged
      newTop = initial.crop_top; // keep top unchanged
      newLeft = initial.crop_left; // keep left unchanged
    } else if (handle === 'w') {
      // Left handle: adjust width and left position
      newWidth = initial.crop_width - deltaXPercent;
      newLeft = initial.crop_left + deltaXPercent;
      newHeight = initial.crop_height; // keep height unchanged
      newTop = initial.crop_top; // keep top unchanged
    } else {
      // Unknown handle: no changes
      return {
        crop_left: initial.crop_left,
        crop_top: initial.crop_top,
        crop_width: initial.crop_width,
        crop_height: initial.crop_height,
      };
    }

    // Enforce minimum size
    newWidth = Math.max(1, newWidth);
    newHeight = Math.max(1, newHeight);

    // Clamp to boundaries so left+width <= 100, top+height <= 100
    newWidth = clamp(newWidth, 1, 100);
    newHeight = clamp(newHeight, 1, 100);
    newLeft = clamp(newLeft, 0, 100 - newWidth);
    newTop = clamp(newTop, 0, 100 - newHeight);

    return {
      crop_left: newLeft,
      crop_top: newTop,
      crop_width: newWidth,
      crop_height: newHeight,
    };
  }, []);

  // Helper function to validate crop area constraints
  const isValidCropArea = useCallback((cropArea) => {
    const { crop_left, crop_top, crop_width, crop_height } = cropArea;
    
    // Check boundaries
    if (crop_left < 0 || crop_top < 0) return false;
    if (crop_left + crop_width > 100 || crop_top + crop_height > 100) return false;
    
    // Check minimum size (1% each dimension)
    if (crop_width < 1 || crop_height < 1) return false;
    
    return true;
  }, []);

  // Setup global mouse event listeners when resizing
  useEffect(() => {
    if (!isResizing && !isDragging) return;

    const handleGlobalMouseMove = (e) => {
      if (isResizing) handleResizeMove(e);
      else if (isDragging) handleDragMove(e);
    };
    const handleGlobalMouseUp = () => {
      if (isResizing) handleResizeEnd();
      if (isDragging) handleDragEnd();
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isResizing, isDragging, handleResizeMove, handleResizeEnd, handleDragMove, handleDragEnd]);

  // Helper function to determine which handles to show based on shape
  const getVisibleHandles = useCallback((_format) => {
    // Show all handles: corners and sides for flexible resizing
    return ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
  }, []);

  // Helper function to render a single resize handle
  const renderHandle = useCallback((handle, cropArea) => {
    const isCornerHandle = ['nw', 'ne', 'sw', 'se'].includes(handle);
    const isSideHandle = ['n', 's', 'e', 'w'].includes(handle);
    
    // Different sizes and shapes for corner vs side handles
    const handleSize = isCornerHandle ? 14 : 12;
    const handleOffset = handleSize / 2;
    
    // Calculate handle position based on type
    let style = {
      position: 'absolute',
      backgroundColor: '#3b82f6',
      border: '2px solid white',
      cursor: getResizeCursor(handle),
      zIndex: Z_INDEX.RESIZE_HANDLE,
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    };

    // Different styling for corner vs side handles
    if (isCornerHandle) {
      // Corner handles: circular
      style.width = `${handleSize}px`;
      style.height = `${handleSize}px`;
      style.borderRadius = '50%';
    } else if (isSideHandle) {
      // Side handles: rectangular, oriented based on direction
      if (handle === 'n' || handle === 's') {
        // Top/bottom handles: wider horizontally
        style.width = '20px';
        style.height = '8px';
        style.borderRadius = '4px';
      } else {
        // Left/right handles: wider vertically  
        style.width = '8px';
        style.height = '20px';
        style.borderRadius = '4px';
      }
    }
    
    // Position the handle based on type
    switch (handle) {
      case 'nw':
        style.left = `${cropArea.left}%`;
        style.top = `${cropArea.top}%`;
        style.transform = `translate(-${handleOffset}px, -${handleOffset}px)`;
        break;
      case 'ne':
        style.left = `${cropArea.left + cropArea.width}%`;
        style.top = `${cropArea.top}%`;
        style.transform = `translate(-${handleOffset}px, -${handleOffset}px)`;
        break;
      case 'sw':
        style.left = `${cropArea.left}%`;
        style.top = `${cropArea.top + cropArea.height}%`;
        style.transform = `translate(-${handleOffset}px, -${handleOffset}px)`;
        break;
      case 'se':
        style.left = `${cropArea.left + cropArea.width}%`;
        style.top = `${cropArea.top + cropArea.height}%`;
        style.transform = `translate(-${handleOffset}px, -${handleOffset}px)`;
        break;
      case 'n':
        style.left = `${cropArea.left + cropArea.width / 2}%`;
        style.top = `${cropArea.top}%`;
        style.transform = `translate(-10px, -4px)`; // Center the 20px wide, 8px tall handle
        break;
      case 's':
        style.left = `${cropArea.left + cropArea.width / 2}%`;
        style.top = `${cropArea.top + cropArea.height}%`;
        style.transform = `translate(-10px, -4px)`; // Center the 20px wide, 8px tall handle
        break;
      case 'e':
        style.left = `${cropArea.left + cropArea.width}%`;
        style.top = `${cropArea.top + cropArea.height / 2}%`;
        style.transform = `translate(-4px, -10px)`; // Center the 8px wide, 20px tall handle
        break;
      case 'w':
        style.left = `${cropArea.left}%`;
        style.top = `${cropArea.top + cropArea.height / 2}%`;
        style.transform = `translate(-4px, -10px)`; // Center the 8px wide, 20px tall handle
        break;
    }
    
    return (
      <div
        key={handle}
        style={style}
        onMouseDown={(e) => !isSavingCrop && handleResizeStart(handle, e)}
        title={`Resize ${handle.toUpperCase()}`}
      />
    );
  }, [getResizeCursor, handleResizeStart, isSavingCrop]);

  // Handle image load to calculate aspect ratio
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatio(aspectRatio);
    setIsImageLoaded(true);
    
    // Update preview dimensions based on aspect ratio
    const containerWidth = previewContainerRef.current?.offsetWidth || 400;
    const maxPreviewWidth = Math.min(containerWidth - 40, 500);
    const maxPreviewHeight = 400;
    
    let previewWidth, previewHeight;
    
    if (aspectRatio > 1) {
      previewWidth = maxPreviewWidth;
      previewHeight = maxPreviewWidth / aspectRatio;
    } else {
      previewHeight = maxPreviewHeight;
      previewWidth = maxPreviewHeight * aspectRatio;
    }
    
    setPreviewDimensions({
      width: Math.min(previewWidth, maxPreviewWidth),
      height: Math.min(previewHeight, maxPreviewHeight)
    });
  }, []);

  // Preview container styles
  const previewContainerStyle = {
    position: 'relative',
    width: `${previewDimensions.width}px`,
    height: `${previewDimensions.height}px`,
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    margin: '0 auto 24px',
    backgroundColor: '#f8fafc',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };

  // Preview image should always show the full image (no zoom),
  // the blue overlay represents the crop selection.
  const imageStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transform: 'none',
  };

  return (
    <div className="field-wrapper">
      {label && <label className={`block mb-3 ${typography.label}`}>{label}</label>}
      
      
      {/* Interactive Preview */}
      <div ref={previewContainerRef} className="mb-6">
        <div style={previewContainerStyle}>
          {currentImageUrl ? (
            <>
              {/* Reset Icon Button - Only show when there are changes */}
              {hasChanges() && !isSavingCrop && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="absolute top-3 right-3 z-50 p-2 rounded-full bg-white dark:bg-dark-800 shadow-lg hover:shadow-xl transition-all hover:scale-110 border border-gray-200 dark:border-dark-600 group"
                  title="Reset crop to full image"
                  aria-label="Reset crop"
                >
                  <svg
                    className="w-5 h-5 text-gray-700 dark:text-dark-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              )}
              
              <img
                ref={imageRef}
                src={currentImageUrl}
                alt="Crop preview"
                style={imageStyle}
                onLoad={handleImageLoad}
                onError={(e) => {
                  if (DEBUG_CROP) {
                    // eslint-disable-next-line no-console
                    console.error('[InteractiveCropField] Image failed to load:', currentImageUrl);
                  }
                  setIsImageLoaded(false);
                }}
                draggable={false}
              />
              
              {/* Shape-aware crop area indicator (always show when image exists) */}
              {currentImageUrl && !isSavingCrop && (
                <div
                  style={{
                    ...getRectOverlayStyle(visibleCropArea),
                    pointerEvents: 'auto',
                    cursor: isDragging ? 'grabbing' : 'grab'
                  }}
                  onMouseDown={(e) => !isSavingCrop && handleDragStart(e)}
                >
                  {/* Center indicator for all shapes */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full opacity-60"></div>
                </div>
              )}

              {/* Interactive resize handles */}
              {currentImageUrl && !isDragging && !isSavingCrop && (
                <>
                  {getVisibleHandles(currentFormat).map(handle => 
                    renderHandle(handle, visibleCropArea)
                  )}
                </>
              )}
              
              {/* Saving overlay to block interactions while backend processes crop */}
              {isSavingCrop && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center" style={{ zIndex: Z_INDEX.EDIT_OVERLAY + 10 }}>
                  <div className="flex items-center gap-2 text-gray-700 dark:text-dark-200">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                    <span className="text-sm">Saving crop...</span>
                  </div>
                </div>
              )}

              {/* Grid overlay removed */}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-dark-400">
              <div className="text-center">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-dark-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm font-medium">No image selected</p>
                <p className={`mt-1 ${typography.caption} text-gray-400 dark:text-dark-500`}>Upload an image to start cropping</p>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Overlay Controls removed (grid/shape info/handle counts) */}
      
      {/* Precision Controls removed */}


      {help && <div className={`mt-4 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}

export default InteractiveCropField;